<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  shopWindows: { type: Array, default: () => [] },
  recentShopWindows: { type: Array, default: () => [] },
  state: { type: String, default: 'pick' }, // 'pick' | 'empty' | 'no-results' | 'success' | 'error' | 'blocked'
  blockedAgentName: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  marketplaceUrl: { type: String, default: '/lais/hub-corretor' },
});

const emit = defineEmits([
  'close',
  'select',
  'create-new',
  'retry',
  'view-all',
  'open-key-holder',
]);

const search = ref('');

watch(
  () => props.show,
  value => {
    if (!value) search.value = '';
  }
);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return props.shopWindows;
  return props.shopWindows.filter(item =>
    (item.name || '').toLowerCase().includes(q)
  );
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    title="Adicionar à vitrine"
    max-width-class="max-w-[560px]"
    @close="emit('close')"
  >
    <template v-if="state === 'blocked'">
      <p class="text-[13px] leading-6 text-lais-text-muted">
        O imóvel já possui um corretor responsável
        <span v-if="blockedAgentName">({{ blockedAgentName }})</span>. Apenas o
        time responsável pelas chaves pode incluí-lo em uma vitrine exclusiva.
      </p>
      <LaisPrimaryButton
        class="mt-3"
        variant="ghost"
        icon="i-lucide-key-round"
        @click="emit('open-key-holder')"
      >
        Ver detalhes das chaves
      </LaisPrimaryButton>
    </template>

    <template v-else-if="state === 'loading' || isLoading">
      <div
        class="flex items-center gap-3 rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-muted"
      >
        <span
          class="i-lucide-loader-circle size-4 animate-spin"
          aria-hidden="true"
        />
        Atualizando vitrines…
      </div>
    </template>

    <template v-else-if="state === 'success'">
      <p class="text-[13px] leading-6 text-emerald-700 font-semibold">
        Tudo certo! O imóvel foi adicionado à vitrine.
      </p>
    </template>

    <template v-else-if="state === 'error'">
      <p class="text-[13px] leading-6 text-rose-700">
        Não foi possível adicionar agora.
      </p>
      <LaisPrimaryButton
        variant="ghost"
        icon="i-lucide-refresh-cw"
        @click="emit('retry')"
      >
        Tentar novamente
      </LaisPrimaryButton>
    </template>

    <template v-else-if="state === 'empty'">
      <p class="text-[13px] leading-6 text-lais-text-muted mb-3">
        Você ainda não tem vitrines criadas. Crie a primeira agora.
      </p>
      <LaisPrimaryButton icon="i-lucide-store" @click="emit('create-new')">
        Criar nova vitrine
      </LaisPrimaryButton>
    </template>

    <template v-else>
      <input
        v-model="search"
        type="search"
        placeholder="Buscar vitrine"
        class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px]"
      />

      <div v-if="recentShopWindows.length" class="mt-3">
        <p
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted mb-1"
        >
          Recentes
        </p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="recent in recentShopWindows"
            :key="recent.id"
            type="button"
            class="rounded-full border border-lais-border px-3 py-1 text-[12px]"
            @click="emit('select', recent)"
          >
            {{ recent.name }}
          </button>
        </div>
      </div>

      <ul class="mt-3 grid gap-2 max-h-[280px] overflow-y-auto">
        <li
          v-if="state === 'no-results' || !filtered.length"
          class="text-[13px] text-lais-text-muted px-3 py-2"
        >
          Nenhuma vitrine encontrada para "{{ search }}".
        </li>
        <li v-for="item in filtered" :key="item.id">
          <button
            type="button"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-left text-[13px] hover:border-lais-primary"
            @click="emit('select', item)"
          >
            <p class="font-semibold">{{ item.name }}</p>
            <p class="text-lais-text-muted">
              {{ item.imovel_count || 0 }} imóveis
            </p>
          </button>
        </li>
      </ul>
    </template>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <a
          :href="marketplaceUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-[12px] text-lais-primary underline"
        >
          Conhecer o marketplace de vitrines
        </a>
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-list"
            @click="emit('view-all')"
          >
            Ver todas
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-plus"
            :is-loading="isLoading"
            @click="emit('create-new')"
          >
            Criar nova vitrine
          </LaisPrimaryButton>
        </div>
      </div>
    </template>
  </LaisModal>
</template>
